import React, { ReactNode } from "react";
import ErrorPage from "../NotFound";
import { ENVIRONMENT } from "../../clientConfig";
import { LOCAL_STORAGE_KEYS, PROJECT_ENVIRONMENT } from "../../constants";
import * as Sentry from "@sentry/react";

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };

    window.addEventListener("beforeunload", () => {
      localStorage.setItem(LOCAL_STORAGE_KEYS.MANUAL_RELOAD, "true");
      const getLocalReload =
        localStorage.getItem(LOCAL_STORAGE_KEYS.RELOAD_COUNT) ?? 0;
      if (getLocalReload > "3") {
        localStorage.setItem(LOCAL_STORAGE_KEYS.RELOAD_COUNT, "0");
      }
    });
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({
      hasError: true,
      error: error,
      errorInfo: errorInfo,
    });

    if (
      ENVIRONMENT === PROJECT_ENVIRONMENT.ENV_STAGE ||
      ENVIRONMENT === PROJECT_ENVIRONMENT.ENV_PRODUCTION
    ) {
      Sentry.withScope((scope) => {
        scope.setContext("reactErrorInfo", {
          componentStack: errorInfo.componentStack,
        });
        Sentry.captureException(error);
      });
    }

    if (localStorage.getItem(LOCAL_STORAGE_KEYS.MANUAL_RELOAD) === "true") {
      this.handleReload();
      localStorage.removeItem(LOCAL_STORAGE_KEYS.MANUAL_RELOAD);
    }
  }

  handleReload = () => {
    const reloadCount = parseInt(
      localStorage.getItem(LOCAL_STORAGE_KEYS.RELOAD_COUNT) ?? "0",
      10
    );
    if (reloadCount < 2) {
      localStorage.setItem(
        LOCAL_STORAGE_KEYS.RELOAD_COUNT,
        (reloadCount + 1).toString()
      );
      window.location.reload();
    } else {
      localStorage.setItem(LOCAL_STORAGE_KEYS.RELOAD_COUNT, "0");
      // window.location.href = "/";
    }
  };

  render() {
    if (this.state.error) {
      if (
        ENVIRONMENT === PROJECT_ENVIRONMENT.ENV_LOCAL ||
        ENVIRONMENT === PROJECT_ENVIRONMENT.ENV_DEVELOPMENT
      ) {
        return (
          <div className="bg-black m-10 p-5 text-white">
            <h3 className="text-red-600">Error: Something is wrong!</h3>
            <div className="text-red-600">{this.state.error.toString()}</div>
            <hr className="my-3 border-2 border-red-400 border-solid" />
            <div>{this.state.errorInfo?.componentStack}</div>
          </div>
        );
      } else {
        return <ErrorPage />;
      }
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
